import React, { useState, useEffect } from "react";
import { getAllJobs } from "../lib/supabase";

export default function JobSelect({
  jobs,
  currentUser,
  orgId,
  onSelectJob,
  onMenuOpen,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("active"); // "active" or "all"
  const [allJobs, setAllJobs] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  // Load all jobs when switching to "all" filter
  useEffect(() => {
    if (filter === "all" && allJobs.length === 0 && orgId) {
      setLoadingAll(true);
      getAllJobs(orgId)
        .then((data) => {
          // Map to same format as active jobs
          const mapped = data.map((job) => ({
            id: job.job_number,
            rawId: job.id,
            name: job.name,
            status: job.status,
            latitude: job.latitude,
            longitude: job.longitude,
            timezone: job.timezone,
            day: 1,
            totalDays: job.estimated_days || 1,
          }));
          setAllJobs(mapped);
        })
        .catch((err) => console.error("Error loading all jobs:", err))
        .finally(() => setLoadingAll(false));
    }
  }, [filter, allJobs.length, orgId]);

  // Use active jobs or all jobs based on filter
  const jobsToShow = filter === "active" ? jobs : allJobs;

  const filteredJobs = jobsToShow.filter(
    (job) =>
      job.id.includes(searchTerm) ||
      job.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onMenuOpen}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            Select Job
          </h1>
        </div>
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          {currentUser?.initials || "??"}
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: "16px" }}>
        <div style={{ position: "relative" }}>
          <svg
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "20px",
              height: "20px",
              color: "#94a3b8",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "12px 16px 12px 48px",
              fontSize: "15px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div
        style={{
          padding: "0 16px 12px",
          display: "flex",
          gap: "8px",
        }}
      >
        <button
          onClick={() => setFilter("active")}
          style={{
            flex: 1,
            padding: "10px 16px",
            borderRadius: "10px",
            border: "none",
            background: filter === "active" ? "#1a5a8e" : "#e2e8f0",
            color: filter === "active" ? "white" : "#64748b",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          Active Jobs
        </button>
        <button
          onClick={() => setFilter("all")}
          style={{
            flex: 1,
            padding: "10px 16px",
            borderRadius: "10px",
            border: "none",
            background: filter === "all" ? "#1a5a8e" : "#e2e8f0",
            color: filter === "all" ? "white" : "#64748b",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          All Jobs
        </button>
      </div>

      {/* GPS Status - only show for active jobs */}
      {filter === "active" && (
        <div
          style={{
            padding: "0 16px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            color: "#64748b",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#10b981",
            }}
          />
          <span>GPS Active • Showing nearest jobs</span>
        </div>
      )}

      {/* Job List */}
      <div
        style={{
          padding: "0 16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {loadingAll && filter === "all" && (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#64748b",
            }}
          >
            Loading all jobs...
          </div>
        )}
        {filteredJobs.map((job) => (
          <button
            key={job.id}
            onClick={() => onSelectJob(job)}
            style={{
              width: "100%",
              background: "white",
              borderRadius: "16px",
              padding: "16px",
              textAlign: "left",
              border: job.closest ? "2px solid #1a5a8e" : "1px solid #e2e8f0",
              boxShadow: job.closest
                ? "0 0 0 3px rgba(26,90,142,0.1)"
                : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "start",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    {job.id}
                  </span>
                  {job.closest && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        background: "#1a5a8e",
                        color: "white",
                        fontSize: "11px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      Closest
                    </span>
                  )}
                  {job.status === "active" && filter === "all" && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        background: "#dcfce7",
                        color: "#166534",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      Active
                    </span>
                  )}
                  {job.day > job.totalDays && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        background: "#fee2e2",
                        color: "#dc2626",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      Over
                    </span>
                  )}
                  {filter === "all" &&
                    job.status &&
                    job.status !== "active" && (
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "6px",
                          background:
                            job.status === "complete" ? "#dcfce7" : "#f1f5f9",
                          color:
                            job.status === "complete" ? "#166534" : "#64748b",
                          fontSize: "11px",
                          fontWeight: "600",
                          textTransform: "capitalize",
                        }}
                      >
                        {job.status}
                      </span>
                    )}
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    color: "#475569",
                    marginBottom: "8px",
                  }}
                >
                  {job.name}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                    </svg>
                    {job.distance}
                  </span>
                  <span>
                    Day {job.day}/{job.totalDays}
                  </span>
                </div>
              </div>
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="#cbd5e1"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
